/**
 * Project color constants
 * Single source of truth for all colors
 * CSS variables are automatically set from this file when the application loads
 */

// Main status colors
export const COLORS = {
  // Green - success, OK, good state
  SUCCESS: '#3ab084',
  SUCCESS_ALPHA_80: '#3ab084cc', // 80% opacity
  SUCCESS_ALPHA_20: '#3ab08433', // 20% opacity

  // Yellow - warning, normal state (for NORM, IDLE, WORK)
  WARNING: '#eee597',
  WARNING_ALPHA_20: '#eee59733', // 20% opacity

  // Yellow - diagnostic warning (more saturated for diagnostics)
  DIAGNOSTIC_WARN: '#fbbf24', // Amber-400 - more saturated yellow
  DIAGNOSTIC_WARN_ALPHA_80: '#fbbf24cc', // 80% opacity
  DIAGNOSTIC_WARN_ALPHA_50: '#fbbf2480', // 50% opacity
  DIAGNOSTIC_WARN_ALPHA_20: '#fbbf2433', // 20% opacity

  // Red - error, critical state
  ERROR: '#e53e3e',
  ERROR_ALPHA_80: '#e53e3ecc', // 80% opacity
  ERROR_ALPHA_50: '#e53e3e80', // 50% opacity
  ERROR_ALPHA_20: '#e53e3e33', // 20% opacity
  ERROR_DARK: '#dc664d', // Darker shade of red

  // Gray - stale state, inactive
  STALE: '#808080',
  STALE_ALPHA_80: '#808080cc', // 80% opacity
  STALE_ALPHA_50: '#80808080', // 50% opacity
  STALE_ALPHA_20: '#80808033', // 20% opacity

  // Blue - charging, informational state
  INFO: '#4a90e2',
  INFO_ALPHA_80: '#4a90e2cc', // 80% opacity
  INFO_ALPHA_20: '#4a90e233', // 20% opacity

  // Neutral colors
  LOCKED: 'rgba(245, 247, 249, 1)',
  UNLOCKED: 'rgba(128, 145, 192, 0.4)',
} as const

/**
 * Mapping of colors to CSS variable names
 */
const COLOR_TO_CSS_VAR: Record<keyof typeof COLORS, string> = {
  SUCCESS: '--color-success',
  SUCCESS_ALPHA_80: '--color-success-alpha-80',
  SUCCESS_ALPHA_20: '--color-success-alpha-20',
  WARNING: '--color-warning',
  WARNING_ALPHA_20: '--color-warning-alpha-20',
  DIAGNOSTIC_WARN: '--color-diagnostic-warn',
  DIAGNOSTIC_WARN_ALPHA_80: '--color-diagnostic-warn-alpha-80',
  DIAGNOSTIC_WARN_ALPHA_50: '--color-diagnostic-warn-alpha-50',
  DIAGNOSTIC_WARN_ALPHA_20: '--color-diagnostic-warn-alpha-20',
  ERROR: '--color-error',
  ERROR_ALPHA_80: '--color-error-alpha-80',
  ERROR_ALPHA_50: '--color-error-alpha-50',
  ERROR_ALPHA_20: '--color-error-alpha-20',
  ERROR_DARK: '--color-error-dark',
  STALE: '--color-stale',
  STALE_ALPHA_80: '--color-stale-alpha-80',
  STALE_ALPHA_50: '--color-stale-alpha-50',
  STALE_ALPHA_20: '--color-stale-alpha-20',
  INFO: '--color-info',
  INFO_ALPHA_80: '--color-info-alpha-80',
  INFO_ALPHA_20: '--color-info-alpha-20',
  LOCKED: '--color-locked',
  UNLOCKED: '--color-unlocked',
}

/**
 * Sets CSS variables from COLORS to document root
 * Called when the application loads
 */
export const setColorCSSVariables = (): void => {
  const root = document.documentElement
  ;(Object.keys(COLORS) as Array<keyof typeof COLORS>).forEach(key => {
    const cssVarName = COLOR_TO_CSS_VAR[key]
    const colorValue = COLORS[key]
    root.style.setProperty(cssVarName, colorValue)
  })
}
